import React, { ElementType, ReactElement } from "react";
import Link from "next/link";
import styles from "./feature.module.scss";
import { blue } from "../../theme";
import { trackEventNext } from "@kunduz/utility-analytics";

export const Feature = (props: Props): ReactElement => {
  const BadgeIcon = props.badgeIcon;

  const PrimaryActionIcon = props.primaryActionIcon;
  const SecondaryActionIcon = props.secondaryActionIcon;

  function handleClicked(label?: string) {
    trackEventNext({
      event: "Feature Component CTA Button Clicked",
      componentName: props.name,
      label,
    });
  }

  return (
    <section
      className={styles.base}
      style={{
        ...(props.variant == "white" && { backgroundColor: "white" }),
        ...(props.variant == "blue" && { backgroundColor: blue[25] }),
      }}
      id={props.id}
    >
      <div className={styles.wrapper}>
        <div className={styles.headingAndActions}>
          <div className={styles.texts}>
            <div className={styles.badge}>
              <BadgeIcon />
              <span className={styles.badgeText}>{props.badgeText}</span>
            </div>
            <p className={styles.title}>{props.title}</p>
          </div>
          <div className={styles.actions}>
            {props.secondaryActionUrl && (
              <Link
                href={props.secondaryActionUrl}
                className={styles.secondaryActionUrl}
                onClick={() => handleClicked(props.secondaryActionTitle)}
              >
                <SecondaryActionIcon />
                {props.secondaryActionTitle}
              </Link>
            )}
            {props.primaryActionUrl && (
              <Link
                href={props.primaryActionUrl}
                className={styles.primaryActionUrl}
                onClick={() => handleClicked(props.primaryActionTitle)}
              >
                <PrimaryActionIcon />
                {props.primaryActionTitle}
              </Link>
            )}
          </div>
        </div>
        <p className={styles.supportingText}>{props.supportingText}</p>
        <div className={styles.mobileActions}>
          {props.secondaryActionUrl && (
            <Link
              href={props.secondaryActionUrl}
              className={styles.mobileSecondaryActionUrl}
              onClick={() => handleClicked(props.secondaryActionTitle)}
            >
              <SecondaryActionIcon />
              {props.secondaryActionTitle}
            </Link>
          )}
          {props.primaryActionUrl && (
            <Link
              href={props.primaryActionUrl}
              className={styles.mobilePrimaryActionUrl}
              onClick={() => handleClicked(props.primaryActionTitle)}
            >
              <PrimaryActionIcon />
              {props.primaryActionTitle}
            </Link>
          )}
        </div>
      </div>
      <div className={styles.wrapper2}>
        <img
          src={props.imageUrl}
          alt={props.title}
          loading="lazy"
          className={styles.image}
          style={{
            order: props.imagePosition == "left" ? 0 : 1,
          }}
        />
        <div className={styles.content}>
          {props.featureItems?.map((fi, i) => {
            const { icon: Icon } = fi;
            return (
              <div key={i} className={styles.featureItem}>
                <Icon className={styles.featureIcon} />
                <div className={styles.featureTexts}>
                  <p className={styles.featureTitle}>{fi.title}</p>
                  <p className={styles.featureLabel}>{fi.label}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className={styles.secondaryImageWrapper}>
        <img
          src={props.secondaryImageUrl}
          loading="lazy"
          className={styles.secondaryImage}
          alt=""
        />
      </div>
    </section>
  );
};

interface Props {
  name: string;
  imagePosition: "left" | "right";
  variant: "white" | "blue";
  badgeText: string;
  badgeIcon: React.ElementType;
  title: string;
  supportingText: string;
  imageUrl: string;
  secondaryImageUrl: string;
  secondaryActionTitle?: string;
  secondaryActionUrl?: string;
  secondaryActionIcon: React.ElementType;
  primaryActionTitle?: string;
  primaryActionUrl?: string;
  primaryActionIcon: React.ElementType;
  featureItems: {
    icon: ElementType;
    title: string;
    label: string;
  }[];
  id?: string;
}
